import type { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import queryString from 'query-string';
import { EMPTY, interval } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  finalize,
  map,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import type { ValueOf } from 'ts-essentials';

import { LoginService } from './login.service';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { DialogConfirmPromptService } from '@libs/src/dialog-confirm-prompt/dialog-confirm-prompt.service';
import { LoggerService } from '@libs/src/logger/logger.service';
import { TenantsBackend } from '@libs/src/tenants/tenants.backend';
import type { SSOSignInParams } from '@libs/src/workos/workos.backend';
import { AuthService } from '@main-client/src/app/auth/auth.service';

import { LoginTokenBody } from './login';
import type {
  LoginResponse,
  LogInTokenCredentials,
} from '@libs/src/interfaces/login-response.interface';
import type { AccountLoginToken } from '@libs/src/models/account-login-token.model';
import type { Account } from '@libs/src/models/account.model';
import type { Tenant } from '@libs/src/tenant/tenant.interface';
import { SetUser } from '@libs/src/user/user.actions';
import { decodeJwt } from '@libs/src/utilities/jwt.utilities';
import type { AppState } from '@main-client/src/app/app.interfaces';
import { getNewAccountRedirectParams } from '@main-client/src/app/auth/auth.helper';

import { getServerError } from '../error-handler/error-handler.helper';
import type { PathwayUnpopulated } from '../models/pathway.interface';

import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import pick from 'lodash-es/pick';
import pickBy from 'lodash-es/pickBy';

const VIEW_MODES = {
  emailSent: 'emailSent',
  enterEmail: 'enterEmail',
} as const;

const httpErrorStatusNotFound = 404;
const loginMethodEmail = 'email';

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit {
  @Input() canSignup: boolean;
  @Input() defaultLoggedInUrl: string;
  @Input() hasSignUpRedirect: boolean;
  @Input() tenant: Tenant;
  VIEW_MODES = VIEW_MODES;
  viewMode: ValueOf<typeof VIEW_MODES> = VIEW_MODES.enterEmail;
  loginForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });
  isUserLoggedIn = false;
  isLoggingIn = false;
  error: { status?: number } = {};
  isLoading = false;
  showSSOButton = false;
  ssoSignInParams: SSOSignInParams = {};
  ssoErrorMessage = '';
  ssoErrorStatus = '';
  tenantHasSSOEnabled = false;

  @ViewChild('createAccountErrorTemplate', { static: true })
  public createAccountErrorTemplate: TemplateRef<void>;

  constructor(
    private readonly destroy$: DestroySubscriptionsService,
    private readonly confirmDialogPrompt: DialogConfirmPromptService,
    private readonly fb: UntypedFormBuilder,
    private readonly loginService: LoginService,
    private readonly logger: LoggerService,
    private readonly recaptchaV3Service: ReCaptchaV3Service,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store<AppState>,
    public readonly authService: AuthService,
    private readonly tenantsBackend: TenantsBackend,
  ) {}

  ngOnInit() {
    const queryParamMap = this.route.snapshot.queryParamMap;
    const loginToken = queryParamMap.get('loginToken');
    const email = queryParamMap.get('email');
    this.isUserLoggedIn = this.authService.$isLoggedIn();
    this.tenantHasSSOEnabled =
      this.tenant.integrations?.workos?.sso_login_enabled;
    if (this.isUserLoggedIn) {
      this.store
        .select('user')
        .pipe(
          filter((user: Account) => !isEmpty(user)),
          take(1),
          tap((user: Account) => this.handleLoggedInUser(loginToken, user)),
        )
        .subscribe();
      return;
    }
    const autoSubmit =
      isEqual('true', queryParamMap.get('autoSubmit')) &&
      !loginToken &&
      !!email;
    if (autoSubmit && this.tenant.email_login_enabled) {
      this.loginForm.get('email').patchValue(email);
      this.sendEmail(this.loginForm);
      return;
    }
    const decodedJwt = decodeJwt<AccountLoginToken>(loginToken);
    const loginMethod = decodedJwt?.login_method || loginMethodEmail;
    const isLoginMethodEmail = loginMethodEmail === loginMethod;
    const decodedEmail = decodedJwt?.email || '';
    const decodedState = decodedJwt?.state || '';
    const isLoginMethodValid =
      !isLoginMethodEmail || this.tenant.email_login_enabled;
    const credentials = this.generateCredentials(loginToken);
    if (isEmpty(credentials) || !isLoginMethodValid) {
      this.showSSOButton = this.shouldShowSSOButton(this.tenant);
      this.ssoErrorMessage =
        this.route.snapshot.queryParamMap.get('ssoErrorMessage');
      this.ssoErrorStatus =
        this.route.snapshot.queryParamMap.get('ssoErrorStatus');
      const pathwayToken =
        this.route.snapshot.queryParamMap.get('pathwayToken');
      this.ssoSignInParams = pickBy({
        destination: pathwayToken
          ? this.joinPathwayState(pathwayToken)
          : this.route.snapshot.queryParamMap.get('redirectUrl') ||
            decodedState,
      });
      return;
    }
    this.loginForm.disable();
    this.isLoggingIn = true;
    this.logger
      .logInfo({
        message: 'Login - Attempt',
        properties: {
          email: decodedEmail,
          isSuperAdmin: !!decodedJwt?.super_admin,
          state: decodedState,
          superAdmin: decodedJwt?.super_admin,
        },
      })
      .subscribe();
    return this.authService
      .login(credentials)
      .pipe(
        tap((response: LoginResponse) =>
          this.store.dispatch(SetUser(response.profile)),
        ),
        tap((response: LoginResponse) => {
          interval(500)
            .pipe(
              map(() => this.authService.$isLoggedIn()),
              filter((isLoggedIn) => isLoggedIn),
              tap((isLoggedIn) => {
                this.isUserLoggedIn = isLoggedIn;
                if (decodedJwt.state) {
                  return this.navigateRoute(decodedJwt.state);
                }
                if (response.isNewAccount) {
                  const { path, queryParams } = getNewAccountRedirectParams(
                    response.profile,
                  );
                  return this.router.navigate(path, { queryParams });
                }
                return this.navigateRoute(this.defaultLoggedInUrl);
              }),
              takeUntil(this.destroy$),
            )
            .subscribe();
        }),
        catchError((response: HttpErrorResponse) => {
          this.isLoading = false;
          return this.onLoginError(response, decodedEmail, decodedState);
        }),
      )
      .subscribe();
  }

  shouldShowSSOButton(tenant: Tenant) {
    if (
      tenant.email_login_enabled &&
      'email' === this.route.snapshot.queryParamMap.get('loginMethod')
    ) {
      return false;
    }
    return tenant.integrations?.workos?.sso_login_enabled;
  }

  handleLoggedInUser(loginToken: string, user: Account) {
    const decodedJwt: AccountLoginToken = decodeJwt(loginToken);
    const validDecodedJwt = !isEmpty(decodedJwt);
    if (
      validDecodedJwt &&
      !isEqual(decodedJwt?.email?.toLowerCase(), user?.email?.toLowerCase())
    ) {
      return this.authService.logout(
        {
          loginTokenPayload: decodedJwt,
          message: 'Different user is logging in',
          user: pick(user, ['id', 'email']),
        },
        () => {
          window.location.reload();
        },
      );
    }
    const validRedirectState = decodedJwt?.state;
    return validRedirectState
      ? this.navigateRoute(validRedirectState)
      : this.router.navigate([this.defaultLoggedInUrl || '/']);
  }

  onLoginError(response: HttpErrorResponse, email: string, state: string) {
    this.error.status = response.status;
    this.loginForm.enable();
    this.isLoggingIn = false;
    if (email) {
      this.loginForm.patchValue({
        email,
      });
    }
    if (state) {
      this.loginForm.addControl('state', this.fb.control(state));
    }

    this.logger
      .logInfo({
        message: 'Login - Failed',
        properties: {
          msg: `Failed login for email: ${email}, reason: ${
            response.error && response.error.message
          }`,
        },
      })
      .subscribe();
    return EMPTY;
  }

  sendEmail(form: UntypedFormGroup) {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    const recaptchaSource = this.recaptchaV3Service.execute('login').pipe(
      take(1),
      catchError((error) => {
        this.isLoading = false;
        this.loginForm.setErrors({ server: error.message });
        return EMPTY;
      }),
    );
    recaptchaSource
      .pipe(
        concatMap((recaptchaToken: string) => {
          const pathwayToken =
            this.route.snapshot.queryParamMap.get('pathwayToken');
          const redirectUrlParam = pathwayToken
            ? this.joinPathwayState(pathwayToken)
            : this.route.snapshot.queryParamMap.get('redirectUrl');
          const loginTokenBody: LoginTokenBody = {
            ...form.value,
            ...(redirectUrlParam && { state: redirectUrlParam }),
            recaptchaToken,
          };
          const isDefaultTenant = 'www' === this.tenant.name;
          const loginRequest = isDefaultTenant
            ? this.tenantsBackend.postTenantsLoginToken(loginTokenBody)
            : this.loginService.getLogInToken(loginTokenBody);
          return loginRequest.pipe(
            catchError((response: HttpErrorResponse) => {
              if (
                httpErrorStatusNotFound === response.status &&
                !!this.hasSignUpRedirect
              ) {
                const { email } = loginTokenBody;
                this.openConfirmEmailDialog(email);
              }
              this.loginForm
                .get('email')
                .setErrors({ server: getServerError(response) });
              return EMPTY;
            }),
            tap(() => {
              this.error = {};
              this.viewMode = VIEW_MODES.emailSent;
            }),
            finalize(() => {
              this.isLoading = false;
            }),
          );
        }),
      )
      .subscribe();
  }

  private openConfirmEmailDialog(email: string) {
    this.confirmDialogPrompt
      .openDialog({
        cancel: 'Try another email',
        color: 'primary',
        confirm: 'Create an account',
        content: this.createAccountErrorTemplate,
        title: "We didn't find your account 🙈",
      })
      .afterClosed()
      .pipe(
        filter((result) => !isEmpty(result)),
        tap(() => {
          this.router.navigate(['/signup'], { queryParams: { email } });
        }),
      )
      .subscribe();
  }

  reset() {
    this.loginForm.reset();
    this.viewMode = VIEW_MODES.enterEmail;
  }

  generateCredentials(loginToken: string): LogInTokenCredentials {
    const decoded = decodeJwt<AccountLoginToken>(loginToken);
    if (isEmpty(decoded)) {
      return;
    }
    return {
      loginToken,
      email: decoded.email,
    };
  }

  navigateRoute(url: string) {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    const [paths, queries] = url.split('?');
    this.router
      .navigate([paths], { queryParams: queryString.parse(queries) })
      .then(() => {
        this.isLoading = false;
      });
  }

  signInWithEmailHandler() {
    this.showSSOButton = false;
  }

  joinPathwayState(joinToken: PathwayUnpopulated['join_token']) {
    return `/pathway-overview?pathwayToken=${joinToken}&isClaimingInvitation=true`;
  }
}
